import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { AppIcon } from '../ui/icon'
import { mdiPaperclip } from '@mdi/js'
import { useAttachDocuments, useDetachDocument } from '../../hooks/useInterventions'
import { AttachDocumentControl, type AttachDocumentControlLabels } from '../documents/AttachDocumentControl'
import { DocumentPreviewCard } from '../documents/DocumentPreviewCard'
import { EmptyState } from '../ui/empty-state'

export function InterventionSectionTitle({ icon, children, testid }: { icon: string; children: React.ReactNode; testid?: string }) {
  return (
    <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold" data-testid={testid}>
      <AppIcon tone="muted" path={icon} size="sm" />
      {children}
    </h3>
  )
}

// --- Documents (§1) — shared between InterventionDetailDrawer and InterventionPage ---
export function InterventionDocumentsSection({
  org,
  assetUid,
  interventionUid,
  documents,
}: {
  org: string
  assetUid: string
  interventionUid: string
  documents: { document_uid: string; document?: { name?: string; file_name?: string; file_mime_type?: string } | null }[]
}) {
  const { t } = useTranslation('assets')
  const attach = useAttachDocuments(org, interventionUid)
  const detach = useDetachDocument(org, interventionUid)

  const linkedUids = useMemo(() => new Set(documents.map((d) => d.document_uid)), [documents])

  const labels: AttachDocumentControlLabels = useMemo(() => ({
    attachExisting: t('interventions.detail.attach_existing', { defaultValue: 'Attacher un document' }),
    upload: t('interventions.detail.upload_new', { defaultValue: 'Téléverser' }),
    uploading: t('interventions.detail.uploading', { defaultValue: 'Envoi…' }),
    pickDocumentTitle: t('interventions.detail.pick_document', { defaultValue: 'Choisir un document' }),
    searchPlaceholder: t('interventions.detail.search_documents', { defaultValue: 'Rechercher (ou coller un lien de document)…' }),
    noDocumentsFound: t('interventions.detail.no_documents_found', { defaultValue: 'Aucun document.' }),
    alreadyAttached: t('interventions.detail.already_attached', { defaultValue: 'déjà attaché' }),
  }), [t])

  return (
    <div>
      <InterventionSectionTitle icon={mdiPaperclip} testid="intervention-documents-title">
        {t('interventions.detail.documents', { defaultValue: 'Documents' })}
      </InterventionSectionTitle>
      {documents.length === 0 ? (
        <EmptyState
          variant="inline"
          className="py-3"
          description={t('interventions.detail.no_documents', { defaultValue: 'Aucun document attaché.' })}
          data-testid="intervention-documents-empty"
        />
      ) : (
        <div className="space-y-2" data-testid="intervention-documents-list">
          {documents.map((d) => (
            <DocumentPreviewCard
              key={d.document_uid}
              org={org}
              documentUid={d.document_uid}
              document={d.document}
              testidPrefix={`intervention-doc-${d.document_uid}`}
              onDetach={() => detach.mutate(d.document_uid)}
            />
          ))}
        </div>
      )}
      <div className="mt-2">
        <AttachDocumentControl
          org={org}
          assetUid={assetUid}
          linkedUids={linkedUids}
          testidPrefix="intervention-doc"
          labels={labels}
          onUploaded={(documentUid) => attach.mutate([documentUid])}
          onSelect={(documentUid) => attach.mutate([documentUid])}
        />
      </div>
    </div>
  )
}
